import type { LedgerEntry } from '@vibetime/runtime';

/**
 * Timetravel works on the per-run ledger chain. Entries are already
 * content-addressed and parent-linked (prevId → id), so indexing them
 * gives us a DAG of one spine plus any fork branches written as extra
 * runs that point back to a prior entry id.
 */

export interface TimelineNode {
  /** The underlying ledger entry. */
  entry: LedgerEntry;
  /** Zero-based index along the linear chain of the owning run. */
  index: number;
  /** Child entry ids within the same run. At most one per chain. */
  children: string[];
  /** Elapsed ms from the first entry of the run. */
  offsetMs: number;
}

export interface TimelineIndex {
  runId: string;
  nodes: TimelineNode[];
  byId: Map<string, TimelineNode>;
  byKind: Map<string, TimelineNode[]>;
  /** Start timestamp (ms) of the first entry in the run, or 0 if empty. */
  startMs: number;
  /** Timestamp of the last entry, or 0 if empty. */
  endMs: number;
}

export function buildTimelineIndex(entries: readonly LedgerEntry[]): TimelineIndex {
  const sorted = [...entries];
  const runId = sorted[0]?.runId ?? '';
  const startMs = sorted[0]?.timestamp ?? 0;
  const endMs = sorted[sorted.length - 1]?.timestamp ?? 0;
  const nodes: TimelineNode[] = sorted.map((entry, index) => ({
    entry,
    index,
    children: [],
    offsetMs: entry.timestamp - startMs,
  }));
  const byId = new Map<string, TimelineNode>();
  for (const node of nodes) byId.set(node.entry.id, node);
  for (const node of nodes) {
    const prevId = node.entry.prevId;
    if (!prevId) continue;
    const parent = byId.get(prevId);
    if (parent) parent.children.push(node.entry.id);
  }
  const byKind = new Map<string, TimelineNode[]>();
  for (const node of nodes) {
    const list = byKind.get(node.entry.kind) ?? [];
    list.push(node);
    byKind.set(node.entry.kind, list);
  }
  return { runId, nodes, byId, byKind, startMs, endMs };
}

/**
 * Walk the chain up to (and including) the checkpoint. Used by every
 * state-projector — plan tree, cost, open tool calls, etc.
 */
export function prefixUpTo(index: TimelineIndex, checkpointId: string): LedgerEntry[] {
  const node = index.byId.get(checkpointId);
  if (!node) return [];
  return index.nodes.slice(0, node.index + 1).map((n) => n.entry);
}

/**
 * Find the node closest to `offsetMs` (ms from run start). Ties break
 * toward the earlier index so shuttle-left feels deterministic.
 */
export function nodeAtOffset(index: TimelineIndex, offsetMs: number): TimelineNode | null {
  if (index.nodes.length === 0) return null;
  let best = index.nodes[0];
  if (!best) return null;
  let bestDelta = Math.abs(best.offsetMs - offsetMs);
  for (const node of index.nodes) {
    const delta = Math.abs(node.offsetMs - offsetMs);
    if (delta < bestDelta) {
      best = node;
      bestDelta = delta;
    }
  }
  return best;
}

/**
 * The spine of a run is the ordered list of ids, used when rendering
 * the scrubber or exporting the HTML bundle.
 */
export function spine(index: TimelineIndex): string[] {
  return index.nodes.map((n) => n.entry.id);
}
